import createBreakpoints from "@material-ui/core/styles/createBreakpoints";
import createPalette from "@material-ui/core/styles/createPalette";
import createTypography from "@material-ui/core/styles/createTypography";
import { Overrides } from "@material-ui/core/styles/overrides";
import { Skin } from "themes/model";
import { createCustomTheme } from "./createCustomTheme";

const logoPath = "https://ringconfigimg.blob.core.windows.net/skins/AnnikDeMey/annikdemey_logo.png";

const headerPath = "https://ringconfigimg.blob.core.windows.net/skins/AnnikDeMey/annikdemey_bg.jpg";

export const palette = createPalette({
  common: {
    white: "#ffffff",
    black: "#3c3c3b",
  },
  primary: {
    light: "#eeeeee",
    main: "#c4a36c",
  },
  text: {
    secondary: "#666666",
    primary: "#3c3c3b",
  },
  error: {
    light: "#FF5833",
    main: "#E43A15",
    dark: "#D02601",
  },
  warning: {
    light: "#FF893E",
    main: "#EF6B20",
    dark: "#DB570C",
  },
  info: {
    light: "#1E97FD",
    main: "#0079DF",
    dark: "#0065CB",
  },
  success: {
    light: "#1EC669",
    main: "#00A84B",
    dark: "#009437",
  },
  background: {
    default: "#ffffff",
  },
  grey: {
    100: "#F7F7F7",
    200: "#EEEEEE",
    300: "#E7E7E7",
    400: "#E4E2E3",
    500: "#B2B2B2",
    600: "#8C8C8C",
    700: "#666666",
    800: "#333333",
    900: "#1F1F1F",
  },
  divider: "#B2B2B2",
});

const fontFamilyHeader = "SourceSans3, sans-serif";
const fontFamily = "SourceSans3, sans-serif";

export const typography = createTypography(palette, {
  h1: {
    fontStyle: "normal",
    fontWeight: 900,
    fontFamily: fontFamilyHeader,
    fontSize: 33,
    lineHeight: 1,
    letterSpacing: 0,
  },
  h2: {
    fontStyle: "normal",
    fontWeight: 900,
    fontFamily: fontFamilyHeader,
    fontSize: "26px",
    lineHeight: 1,
    letterSpacing: 0,
  },
  h3: {
    fontStyle: "normal",
    fontWeight: 400,
    fontFamily: fontFamilyHeader,
    fontSize: "20px",
    lineHeight: 1,
    letterSpacing: 0,
  },
  h4: {
    fontFamily: fontFamilyHeader,
    fontStyle: "normal",
    fontSize: "18px",
    lineHeight: 1,
    fontWeight: 400,
    letterSpacing: 0,
  },
  h5: {
    fontFamily: fontFamilyHeader,
    fontStyle: "normal",
    fontSize: "16px",
    lineHeight: 1.2,
    fontWeight: 700,
    letterSpacing: 0,
  },
  h6: {
    fontFamily: fontFamilyHeader,
    fontStyle: "normal",
    fontSize: "16px",
    fontWeight: 900,
    lineHeight: 1,
    letterSpacing: 0,
  },
  subtitle1: {
    fontFamily,
    fontStyle: "normal",
    fontSize: "12px",
    lineHeight: 1.2,
    fontWeight: 400,
    letterSpacing: 0,
  },
  subtitle2: {
    fontFamily,
    fontStyle: "normal",
    fontSize: "22px",
    lineHeight: 1.2,
    fontWeight: 400,
    letterSpacing: 0,
    textTransform: "uppercase" as "uppercase",
  },
  button: {
    fontFamily,
    fontStyle: "normal",
    fontSize: "16px",
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: "1px",
  },
  caption: {
    fontFamily,
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: 1.4,
    letterSpacing: 0,
  },
  body1: {
    fontFamily,
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: 1.4,
    letterSpacing: 0,
  },
  body2: {
    fontFamily,
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: 1.4,
    letterSpacing: 0,
    "-webkit-font-smoothing": "subpixel-antialiased",
  },
});

const shape = {
  borderRadius: 0,
};

const breakpoints = createBreakpoints({
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1206,
    xl: 1920,
  },
});

const overrides: Overrides = {
  MuiCssBaseline: {
    "@global": {
      "@font-face": [
		/* cyrillic-ext */
{
  fontFamily: "SourceSans3",
  fontStyle: "italic",
  fontWeight: 400,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpDtKy2OAdR1K-IwhWudF-R3woAa8opPOrG97lwqLlOxCQSmrfB.woff2) format('woff2')",
  unicodeRange: "U+0460-052F, U+1C80-1C8A, U+20B4, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F",
},
/* cyrillic */
{
  fontFamily: "SourceSans3",
  fontStyle: "italic",
  fontWeight: 400,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpDtKy2OAdR1K-IwhWudF-R3woAa8opPOrG97lwqLlOxC0SmrfB.woff2) format('woff2')",
  unicodeRange: "U+0301, U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116",
},
/* greek-ext */
{
  fontFamily: "SourceSans3",
  fontStyle: "italic",
  fontWeight: 400,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpDtKy2OAdR1K-IwhWudF-R3woAa8opPOrG97lwqLlOxCUSmrfB.woff2) format('woff2')",
  unicodeRange: "U+1F00-1FFF",
},
/* greek */
{
  fontFamily: "SourceSans3",
  fontStyle: "italic",
  fontWeight: 400,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpDtKy2OAdR1K-IwhWudF-R3woAa8opPOrG97lwqLlOxCoSmrfB.woff2) format('woff2')",
  unicodeRange: "U+0370-0377, U+037A-037F, U+0384-038A, U+038C, U+038E-03A1, U+03A3-03FF",
},
/* vietnamese */
{
  fontFamily: "SourceSans3",
  fontStyle: "italic",
  fontWeight: 400,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpDtKy2OAdR1K-IwhWudF-R3woAa8opPOrG97lwqLlOxCYSmrfB.woff2) format('woff2')",
  unicodeRange: "U+0102-0103, U+0110-0111, U+0128-0129, U+0168-0169, U+01A0-01A1, U+01AF-01B0, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+1EA0-1EF9, U+20AB",
},
/* latin-ext */
{
  fontFamily: "SourceSans3",
  fontStyle: "italic",
  fontWeight: 400,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpDtKy2OAdR1K-IwhWudF-R3woAa8opPOrG97lwqLlOxCcSmrfB.woff2) format('woff2')",
  unicodeRange: "U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C4, U+2113, U+2C60-2C7F, U+A720-A7FF",
},
/* latin */
{
  fontFamily: "SourceSans3",
  fontStyle: "italic",
  fontWeight: 400,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpDtKy2OAdR1K-IwhWudF-R3woAa8opPOrG97lwqLlOxCkSmg.woff2) format('woff2')",
  unicodeRange: "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD",
},
/* cyrillic-ext */
{
  fontFamily: "SourceSans3",
  fontStyle: "normal",
  fontWeight: 400,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpBtKy2OAdR1K-IwhWudF-R9QMylBJAV3Bo8Ky462EH9CsKng.woff2) format('woff2')",
  unicodeRange: "U+0460-052F, U+1C80-1C8A, U+20B4, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F",
},
/* cyrillic */
{
  fontFamily: "SourceSans3",
  fontStyle: "normal",
  fontWeight: 400,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpBtKy2OAdR1K-IwhWudF-R9QMylBJAV3Bo8Ky462EO9CsKng.woff2) format('woff2')",
  unicodeRange: "U+0301, U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116",
},
/* greek-ext */
{
  fontFamily: "SourceSans3",
  fontStyle: "normal",
  fontWeight: 400,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpBtKy2OAdR1K-IwhWudF-R9QMylBJAV3Bo8Ky462EG9CsKng.woff2) format('woff2')",
  unicodeRange: "U+1F00-1FFF",
},
/* greek */
{
  fontFamily: "SourceSans3",
  fontStyle: "normal",
  fontWeight: 400,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpBtKy2OAdR1K-IwhWudF-R9QMylBJAV3Bo8Ky462EJ9CsKng.woff2) format('woff2')",
  unicodeRange: "U+0370-0377, U+037A-037F, U+0384-038A, U+038C, U+038E-03A1, U+03A3-03FF",
},
/* vietnamese */
{
  fontFamily: "SourceSans3",
  fontStyle: "normal",
  fontWeight: 400,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpBtKy2OAdR1K-IwhWudF-R9QMylBJAV3Bo8Ky462EF9CsKng.woff2) format('woff2')",
  unicodeRange: "U+0102-0103, U+0110-0111, U+0128-0129, U+0168-0169, U+01A0-01A1, U+01AF-01B0, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+1EA0-1EF9, U+20AB",
},
/* latin-ext */
{
  fontFamily: "SourceSans3",
  fontStyle: "normal",
  fontWeight: 400,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpBtKy2OAdR1K-IwhWudF-R9QMylBJAV3Bo8Ky462EE9CsKng.woff2) format('woff2')",
  unicodeRange: "U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C4, U+2113, U+2C60-2C7F, U+A720-A7FF",
},
/* latin */
{
  fontFamily: "SourceSans3",
  fontStyle: "normal",
  fontWeight: 400,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpBtKy2OAdR1K-IwhWudF-R9QMylBJAV3Bo8Ky462EK9Cs.woff2) format('woff2')",
  unicodeRange: "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD",
},
/* cyrillic-ext */
{
  fontFamily: "SourceSans3",
  fontStyle: "normal",
  fontWeight: 700,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpBtKy2OAdR1K-IwhWudF-R9QMylBJAV3Bo8Kxf7GEH9CsKng.woff2) format('woff2')",
  unicodeRange: "U+0460-052F, U+1C80-1C8A, U+20B4, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F",
},
/* cyrillic */
{
  fontFamily: "SourceSans3",
  fontStyle: "normal",
  fontWeight: 700,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpBtKy2OAdR1K-IwhWudF-R9QMylBJAV3Bo8Kxf7GEO9CsKng.woff2) format('woff2')",
  unicodeRange: "U+0301, U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116",
},
/* greek-ext */
{
  fontFamily: "SourceSans3",
  fontStyle: "normal",
  fontWeight: 700,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpBtKy2OAdR1K-IwhWudF-R9QMylBJAV3Bo8Kxf7GEG9CsKng.woff2) format('woff2')",
  unicodeRange: "U+1F00-1FFF",
},
/* greek */
{
  fontFamily: "SourceSans3",
  fontStyle: "normal",
  fontWeight: 700,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpBtKy2OAdR1K-IwhWudF-R9QMylBJAV3Bo8Kxf7GEJ9CsKng.woff2) format('woff2')",
  unicodeRange: "U+0370-0377, U+037A-037F, U+0384-038A, U+038C, U+038E-03A1, U+03A3-03FF",
},
/* vietnamese */
{
  fontFamily: "SourceSans3",
  fontStyle: "normal",
  fontWeight: 700,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpBtKy2OAdR1K-IwhWudF-R9QMylBJAV3Bo8Kxf7GEF9CsKng.woff2) format('woff2')",
  unicodeRange: "U+0102-0103, U+0110-0111, U+0128-0129, U+0168-0169, U+01A0-01A1, U+01AF-01B0, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+1EA0-1EF9, U+20AB",
},
/* latin-ext */
{
  fontFamily: "SourceSans3",
  fontStyle: "normal",
  fontWeight: 700,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpBtKy2OAdR1K-IwhWudF-R9QMylBJAV3Bo8Kxf7GEE9CsKng.woff2) format('woff2')",
  unicodeRange: "U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C4, U+2113, U+2C60-2C7F, U+A720-A7FF",
},
/* latin */
{
  fontFamily: "SourceSans3",
  fontStyle: "normal",
  fontWeight: 700,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpBtKy2OAdR1K-IwhWudF-R9QMylBJAV3Bo8Kxf7GEK9Cs.woff2) format('woff2')",
  unicodeRange: "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD",
},
/* cyrillic-ext */
{
  fontFamily: "SourceSans3",
  fontStyle: "italic",
  fontWeight: 700,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpDtKy2OAdR1K-IwhWudF-R3woAa8opPOrG97lwqF5JxCQSmrfB.woff2) format('woff2')",
  unicodeRange: "U+0460-052F, U+1C80-1C8A, U+20B4, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F",
},
/* cyrillic */
{
  fontFamily: "SourceSans3",
  fontStyle: "italic",
  fontWeight: 700,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpDtKy2OAdR1K-IwhWudF-R3woAa8opPOrG97lwqF5JxC0SmrfB.woff2) format('woff2')",
  unicodeRange: "U+0301, U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116",
},
/* greek-ext */
{
  fontFamily: "SourceSans3",
  fontStyle: "italic",
  fontWeight: 700,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpDtKy2OAdR1K-IwhWudF-R3woAa8opPOrG97lwqF5JxCUSmrfB.woff2) format('woff2')",
  unicodeRange: "U+1F00-1FFF",
},
/* greek */
{
  fontFamily: "SourceSans3",
  fontStyle: "italic",
  fontWeight: 700,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpDtKy2OAdR1K-IwhWudF-R3woAa8opPOrG97lwqF5JxCoSmrfB.woff2) format('woff2')",
  unicodeRange: "U+0370-0377, U+037A-037F, U+0384-038A, U+038C, U+038E-03A1, U+03A3-03FF",
},
/* vietnamese */
{
  fontFamily: "SourceSans3",
  fontStyle: "italic",
  fontWeight: 700,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpDtKy2OAdR1K-IwhWudF-R3woAa8opPOrG97lwqF5JxCYSmrfB.woff2) format('woff2')",
  unicodeRange: "U+0102-0103, U+0110-0111, U+0128-0129, U+0168-0169, U+01A0-01A1, U+01AF-01B0, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+1EA0-1EF9, U+20AB",
},
/* latin-ext */
{
  fontFamily: "SourceSans3",
  fontStyle: "italic",
  fontWeight: 700,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpDtKy2OAdR1K-IwhWudF-R3woAa8opPOrG97lwqF5JxCcSmrfB.woff2) format('woff2')",
  unicodeRange: "U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C4, U+2113, U+2C60-2C7F, U+A720-A7FF",
},
/* latin */
{
  fontFamily: "SourceSans3",
  fontStyle: "italic",
  fontWeight: 700,
  fontDisplay: "swap",
  src: "url(https://fonts.gstatic.com/s/sourcesans3/v20/nwpDtKy2OAdR1K-IwhWudF-R3woAa8opPOrG97lwqF5JxCkSmg.woff2) format('woff2')",
  unicodeRange: "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD",
},
      ],
      ".value-icon":
      {
        height: ".75em"
      },
	  "div[class^='makeStyles-filter-']":
	  {
		color: "#3c3c3b",
		fontWeight: 400
	  },
	  "div[class^='makeStyles-thumbnail-']": 
	  {
		borderColor: "#E4E2E3"
	  },
	  "legend.MuiFormLabel-root, .MuiSvgIcon-root, .MuiSvgIcon-root, label.MuiFormLabel-root, h4":
	  {
		color: "#3c3c3b",
		textTransform: "uppercase"
	  },
	  ".MuiButton-root":
	  {
		fontFamily: fontFamilyHeader,
		fontSize: 14
	  },
	  ".MuiTypography-colorTextSecondary, .MuiListItemIcon-root > svg":
	  {
		color: "#61605a"
	  },
	  ".MuiChip-colorPrimary, .MuiButton-containedPrimary":
	  {
		backgroundColor: "#ffffff",
		color: "#aaaaaa"
	  },
	  ".MuiChip-colorPrimary:hover, .MuiButton-containedPrimary:hover":
	  {
		backgroundColor: "#ccc28a",
		color: "#ffffff"
	  },
	  ".MuiChip-deleteIconColorPrimary, .MuiListItemText-primary":
	  {
		fontFamily: fontFamilyHeader,
		color: "#ccc28a",
		fontSize: 12
	  }/*,
	  ".MuiToolbar-regular":
	  {
		minHeight: "136px !important"
	  },
	  "img[class^='makeStyles-logo-']":
	  {
		maxHeight: 62
	  }*/
    },
  },
  MuiAppBar: {
    colorDefault: { 
      backgroundColor: "#ffffff"
    },
  },
  MuiButton: {
    outlinedPrimary: { 
      color: "#343C48",
      borderColor: "#343C48",
    },
    containedPrimary: {
      backgroundColor: "#ccc28a",
      borderColor: "#343C48",
    },
  }
};

export const skin: Skin = {
  logoPath,
  headerPath,
  headerHeight: 350,
  theme: createCustomTheme(palette, typography, shape, breakpoints, overrides),
};
